// frontend/src/components/technical_reports/LaudoEpis.js
import React, { useState, useEffect } from 'react';

// --- IMPORTANTE: Certifique-se que estas imagens estão na pasta ---
import logoLaudoDefault from './logo_laudo.png';
import assinaturaTecnico from './assinatura_tecnico.png';

// --- COMPONENTES AUXILIARES ---
const InputCell = ({ field, data, onChange, readOnly, type = 'text', align = 'center', style = {}, placeholder = '', valueOverride, ...rest }) => (
  <input
    type={type}
    value={valueOverride !== undefined ? valueOverride : (data[field] || '')}
    onChange={(e) => !readOnly && onChange(field, e.target.value)}
    disabled={readOnly}
    placeholder={readOnly ? '' : placeholder}
    className="print-input"
    style={{
      width: '100%',
      height: '100%',
      border: 'none',
      background: readOnly ? 'transparent' : 'rgba(0, 123, 255, 0.1)',
      fontFamily: 'Calibri, Arial, sans-serif',
      fontSize: 'inherit',
      color: '#000000',
      fontWeight: 'bold',
      textAlign: align,
      outline: 'none',
      boxSizing: 'border-box',
      padding: '0 2px',
      ...style
    }}
    {...rest}
  />
);

// --- HELPER DE IMAGEM (CORRIGIDO PARA DRIVE E IMPRESSÃO) ---
const getDirectImageUrl = (url) => {
    if (!url) return '';
    
    // Tratamento especial para URLs locais (Uploads do servidor)
    if (url.startsWith('/api/uploads/')) {
        // Retorna a URL exata com a origem atual para evitar erro de Cross-Origin no html2canvas
        return window.location.origin + url;
    }

    if (url.match(/\.(jpeg|jpg|gif|png)$/) != null) return url; 
    
    // Expressão regular aprimorada para capturar o ID do Drive de várias formas comuns
    const idMatch = url.match(/(?:drive\.google\.com\/(?:file\/d\/|open\?id=)|docs\.google\.com\/uc\?id=)([a-zA-Z0-9_-]+)/);
    
    if (idMatch && idMatch[1]) {
        // O endpoint 'thumbnail' com parâmetro de tamanho (sz=w1000) burla o bloqueio de CORS do Drive
        return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w1000`;
    }
    return url;
};

// --- COMPONENTE PRINCIPAL ---
const LaudoEpis = ({ data = {}, onChange, readOnly = false, availableEpis = [], onEpiSelect, photos = [], currentUser }) => {
  const safeData = data || {};
  
  // Estado para o Menu de Contexto
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, type: '', rowIdx: 0, fieldKey: '' });
  
  // ESTADOS DE ASSINATURA E LAYOUT
  const [availableSignatures, setAvailableSignatures] = useState([]);
  const [reportLayout, setReportLayout] = useState({});

  // --- EFEITO: BUSCAR CONFIGURAÇÕES E ASSINATURAS ---
  useEffect(() => {
      const initializeLaudo = async () => {
          let initialUpdates = {};
          let hasUpdates = false;

          const token = localStorage.getItem('token');

          // --- DECODIFICA O TOKEN PARA DESCOBRIR O ID DO USUÁRIO LOGADO ---
          let currentUserId = currentUser?.id || null;
          if (!currentUserId && token) {
              try {
                  const base64Url = token.split('.')[1];
                  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                  }).join(''));
                  const payload = JSON.parse(jsonPayload);
                  currentUserId = payload.id;
              } catch (e) {
                  console.error("Erro ao decodificar token", e);
              }
          }

          // 1. Busca o Layout do Laudo
          try {
              const layoutResponse = await fetch('/api/report-layout-settings', {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (layoutResponse.ok) {
                  const layoutData = await layoutResponse.json();
                  setReportLayout(layoutData);
              }
          } catch (e) {
              console.error("Erro ao buscar layout do laudo:", e);
          }

          // 2. Busca as assinaturas disponíveis
          try {
              const sigResponse = await fetch('/api/users/signatures/available', {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (sigResponse.ok) {
                  const signaturesFetched = await sigResponse.json();
                  setAvailableSignatures(signaturesFetched);

                  const techsOnly = signaturesFetched.filter(s => s.is_responsible_engineer != 1 && s.is_responsible_engineer !== true && s.is_responsible_engineer !== '1');
                  const engsOnly = signaturesFetched.filter(s => s.is_responsible_engineer == 1 || s.is_responsible_engineer === true || s.is_responsible_engineer === '1');

                  // Verifica se é um laudo NOVO (em branco) ou se estamos editando um ANTIGO
                  const isNewReport = safeData.rowCount === undefined;

                  // --- PREENCHIMENTO AUTOMÁTICO (APENAS PARA LAUDOS NOVOS / SEM ASSINATURA) ---
                  if (!safeData.tecnico_assinatura_id && isNewReport) {
                      const myTechSig = techsOnly.find(s => String(s.id) === String(currentUserId));
                      if (myTechSig) {
                          initialUpdates.tecnico_assinatura_id = String(myTechSig.id);
                          hasUpdates = true;
                      } 
                  }

                  if (!safeData.engenheiro_assinatura_id && isNewReport) {
                      const myEngSig = engsOnly.find(s => String(s.id) === String(currentUserId));
                      if (myEngSig) {
                          initialUpdates.engenheiro_assinatura_id = String(myEngSig.id);
                          hasUpdates = true;
                      } 
                  }
              }
          } catch (e) { 
              console.error("Erro ao buscar assinaturas:", e); 
          }

          if (readOnly) return; 

          // 3. Prepara os dados padrões se estiverem vazios
          const defaultValues = {
              descricao_ensaio: 'Os equipamentos foram ensaiados conforme ASTM F 711. Foi aplicada uma tensão de 50kv, a uma taxa de elevação de no máximo 3kv/s durante o tempo de 60s.',
              rowCount: 5
          };

          Object.entries(defaultValues).forEach(([key, value]) => {
              if (safeData[key] === undefined) {
                  initialUpdates[key] = value;
                  hasUpdates = true;
              }
          });

          // 4. Dispara a atualização em um lote único
          if (hasUpdates) {
              onChange({ ...safeData, ...initialUpdates });
          }
      };

      initializeLaudo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateData = (updates) => {
      if (readOnly) return;
      onChange({ ...safeData, ...updates });
  };

  const handleChange = (field, value) => {
      updateData({ [field]: value });
  };

  // --- LÓGICA DE MUDANÇA DA ASSINATURA DO TÉCNICO ---
  const handleSignatureSelection = (e) => {
      const sigId = e.target.value;
      
      // Captura também os dados exatos do técnico no momento da seleção para "congelar" no laudo
      const selectedTech = availableSignatures.find(s => String(s.id) === String(sigId));
      
      updateData({ 
          tecnico_assinatura_id: sigId,
          tecnico_nome: selectedTech ? selectedTech.signature_name : '',
          tecnico_funcao: selectedTech ? selectedTech.signature_role : '',
          tecnico_registro: selectedTech ? selectedTech.signature_registry : '',
          tecnico_imagem: selectedTech ? selectedTech.signature_image_path : ''
      });
  };

  // --- LÓGICA DE MUDANÇA DA ASSINATURA DO ENGENHEIRO ---
  const handleEngineerSelection = (e) => {
      const sigId = e.target.value;
      
      // Captura também os dados exatos do engenheiro no momento da seleção para "congelar" no laudo
      const selectedEng = availableSignatures.find(s => String(s.id) === String(sigId));

      updateData({ 
          engenheiro_assinatura_id: sigId,
          engenheiro_nome: selectedEng ? selectedEng.signature_name : '',
          engenheiro_funcao: selectedEng ? selectedEng.signature_role : '',
          engenheiro_registro: selectedEng ? selectedEng.signature_registry : '',
          engenheiro_imagem: selectedEng ? selectedEng.signature_image_path : ''
      });
  };

  // --- LÓGICA DE SELEÇÃO DO ITEM ---
  const handleSelectionChange = (e) => {
      const selectedName = e.target.value;
      const selectedItem = availableEpis.find(t => t.descricao === selectedName);
      
      let updates = {
          item_servico_selecionado: selectedName
      };

      if (selectedItem) {
          updates.nome_equipamento = selectedItem.descricao;
          if (selectedItem.applied_standards) {
              updates.norma_aplicavel = selectedItem.applied_standards;
          }
          if (selectedItem.test_performed) {
              updates.descricao_ensaio = selectedItem.test_performed;
          }
      } else {
          updates.nome_equipamento = '';
          updates.norma_aplicavel = '';
      }
      updateData(updates);
      if (onEpiSelect) {
          onEpiSelect(selectedName);
      }
  };

  // --- VERIFICAÇÕES DE TIPO DE ITEM ---
  const selectedItemUpper = (safeData.item_servico_selecionado || '').toUpperCase();
  const isLiner = selectedItemUpper.includes('LINER');
  const isLoadbuster = selectedItemUpper.includes('LOADBUSTER');

  const commonProps = {
    data: safeData,
    onChange: handleChange,
    readOnly: readOnly
  };

  const statusValue = safeData['status_equipamento'] || 'APROVADO';
  const isAprovado = statusValue === 'APROVADO';
  const rowCount = safeData.rowCount !== undefined ? safeData.rowCount : 5;

  // --- GERENCIAMENTO DE LINHAS ---
  const handleAddRow = () => {
      if (readOnly) return;
      handleChange('rowCount', rowCount + 1);
  };

  const handleRemoveRow = () => {
      if (readOnly) return;
      if (rowCount > 1) {
          handleChange('rowCount', rowCount - 1);
      }
  };

  const handleRowCountChange = (e) => {
      if (readOnly) return;
      const val = parseInt(e.target.value);
      if (!isNaN(val) && val > 0) {
          handleChange('rowCount', val);
      }
  };

  // --- LÓGICA DO MENU DE CONTEXTO ---
  const handleRightClick = (e, type, rowIdx, fieldKey) => {
      e.preventDefault(); 
      if (readOnly) return;

      setContextMenu({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type, 
          rowIdx,
          fieldKey
      });
  };

  const closeContextMenu = () => {
      setContextMenu({ ...contextMenu, visible: false });
  };

  const handleReplicate = (action) => {
      const { rowIdx, fieldKey, type } = contextMenu;
      const startValue = safeData[fieldKey];
      const updates = {};

      if (!startValue && startValue !== 0) {
          alert("Preencha o campo antes de replicar.");
          closeContextMenu();
          return;
      }

      for (let i = rowIdx + 1; i < rowCount; i++) {
          const targetKey = `item_${i}_${type}`;
          
          if (action === 'sequence') {
              const match = String(startValue).match(/(\d+)$/);
              if (match) {
                  const numberPart = match[1]; 
                  const prefix = startValue.substring(0, match.index); 
                  const numberVal = parseInt(numberPart, 10); 
                  const offset = i - rowIdx;
                  const newNum = numberVal + offset;
                  const padding = numberPart.length;
                  updates[targetKey] = prefix + String(newNum).padStart(padding, '0');
              } else {
                  updates[targetKey] = startValue; 
              }
          } else {
              updates[targetKey] = startValue;
          }
      }

      updateData(updates);
      closeContextMenu();
  };

  // --- RENDERIZAÇÃO DAS ASSINATURAS (CRUZANDO ID DO BANCO COM DADOS ATUAIS DA API) ---
  const engSignatureObj = availableSignatures.find(s => String(s.id) === String(safeData.engenheiro_assinatura_id));

  // Se encontrou o usuário na API, usa os dados dele (assim sempre fica atualizado se ele mudar a foto/nome no perfil).
  // Se não encontrou, usa o que tá gravado no JSON (safeData). Se não tiver nada, fica vazio.
  const finalEngImagemRaw = engSignatureObj?.signature_image_path || safeData.engenheiro_imagem || '';
  const finalEngNome = engSignatureObj?.signature_name || safeData.engenheiro_nome || '';
  const finalEngFuncao = engSignatureObj?.signature_role || safeData.engenheiro_funcao || '';
  const finalEngRegistro = engSignatureObj?.signature_registry || safeData.engenheiro_registro || '';

  // Applica a função Helper na imagem para garantir que o html2canvas consegue processar o caminho
  const finalEngImagem = finalEngImagemRaw ? getDirectImageUrl(finalEngImagemRaw) : '';

  return (
    <div className="laudo-container-outer no-print-bg" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={closeContextMenu}>
      <div className="laudo-container" id="laudo-epi-pdf-container">

      {/* --- MENU DE CONTEXTO (POPUP) --- */}
      {contextMenu.visible && (
          <div 
              className="context-menu"
              style={{
                  position: 'fixed',
                  top: contextMenu.y,
                  left: contextMenu.x,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  boxShadow: '2px 2px 5px rgba(0,0,0,0.2)',
                  zIndex: 9999,
                  padding: '5px 0',
                  borderRadius: '4px',
                  minWidth: '160px'
              }}
              onClick={(e) => e.stopPropagation()} 
          >
              <div style={{ padding: '8px 15px', cursor: 'default', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '12px', color: '#666' }}>
                  Ações Rápidas
              </div>
              
              <button 
                  onClick={() => handleReplicate('sequence')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 15px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px' }}
                  className="menu-item-hover"
              >
                  🔢 Preencher Sequência
              </button>
              
              <button 
                  onClick={() => handleReplicate('copy')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 15px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px' }}
                  className="menu-item-hover"
              >
                  📋 Replicar Valor (Copiar)
              </button>
              
              <button 
                  onClick={closeContextMenu}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 15px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', color: 'red' }}
                  className="menu-item-hover"
              >
                  Cancelar
              </button>
          </div>
      )}

      {/* --- SELETOR DE ITEM E ASSINATURA --- */}
      {!readOnly && (
        <div className="no-print" style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: safeData.item_servico_selecionado ? '#f0f9ff' : '#fff0f0', 
            border: safeData.item_servico_selecionado ? '1px solid #bae6fd' : '1px solid #feb2b2',
            borderRadius: '5px' 
        }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#0369a1' }}>
                Vincular Item da OS (EPI/EPC): <span style={{color: 'red'}}>*</span>
            </label>
            <select
                value={safeData.item_servico_selecionado || ''}
                onChange={handleSelectionChange} 
                style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: safeData.item_servico_selecionado ? '1px solid #ccc' : '2px solid red'
                }}
            >
                <option value="">-- Selecione um item da OS (Obrigatório) --</option>
                {availableEpis.map((epi, idx) => (
                    <option key={idx} value={epi.descricao}>
                        {epi.descricao}
                    </option>
                ))}
            </select>
            {!safeData.item_servico_selecionado && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
                    ⚠ É obrigatório selecionar um item para gerar o laudo corretamente.
                </div>
            )}

            <div style={{ marginTop: '15px', borderTop: '1px dashed #ccc', paddingTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#0369a1' }}>
                        Responsável Técnico (Emissão):
                    </label>
                    <select
                        value={safeData.tecnico_assinatura_id || ''}
                        onChange={handleSignatureSelection} 
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="">-- Assinatura Padrão / Em Branco --</option>
                        {availableSignatures.filter(s => s.is_responsible_engineer != 1 && s.is_responsible_engineer !== true && s.is_responsible_engineer !== '1').map((sig, idx) => (
                            <option key={idx} value={sig.id}>
                                {sig.signature_name} - {sig.signature_role}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#0369a1' }}>
                        Engenheiro Responsável (Aprovação):
                    </label>
                    <select
                        value={safeData.engenheiro_assinatura_id || ''}
                        onChange={handleEngineerSelection} 
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="">-- Assinatura Padrão / Em Branco --</option>
                        {availableSignatures.filter(s => s.is_responsible_engineer == 1 || s.is_responsible_engineer === true || s.is_responsible_engineer === '1').map((sig, idx) => (
                            <option key={idx} value={sig.id}>
                                {sig.signature_name} - {sig.signature_role}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
      )}

      {/* CSS DE IMPRESSÃO E GERAL COM ZOOM DE TELA E TRATAMENTO MOBILE */}
      <style>{`
        .menu-item-hover:hover {
            background-color: #f0f0f0 !important;
        }

        @media screen {
            .laudo-container-outer {
                background-color: #ffffff !important;
                padding: 0 !important;
                display: block !important;
                overflow-x: auto !important;
                -webkit-overflow-scrolling: touch !important; 
            }
            .laudo-container {
                width: 100% !important;
                min-width: 1000px !important; 
                max-width: 100% !important;
                box-shadow: none !important;
                margin: 0 auto !important; 
                transform: none !important;
                background-color: white !important;
                padding: 10px !important;
            }
            .laudo-table {
                font-size: 16px !important; 
            }
            .laudo-table td {
                padding: 6px 4px !important; 
            }
            .print-input {
                font-size: 14px !important; 
            }
            .status-select {
                font-size: 18px !important;
            }
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          html, body {
            height: auto !important;
            min-height: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background-color: white !important;
            -webkit-print-color-adjust: exact;
            overflow: visible !important;
          }

          .print-input {
            background-color: transparent !important;
          }

          select {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            border: none;
            background: transparent;
          }
          
          .no-print { display: none !important; }
          .context-menu { display: none !important; }
        
          img[alt="Assinatura"] { max-width: 80px !important; }
        
          .laudo-container-outer { 
              background-color: white !important; 
              padding: 0 !important; 
              display: block !important; 
              width: 100% !important;
              overflow: visible !important;
          }

          .laudo-container {
             width: 100% !important; 
             min-width: 0 !important;
             margin: 0 !important; 
             padding: 0 !important;
             transform: scale(1); 
             transform-origin: top left; 
             overflow: visible !important;
             box-shadow: none !important;
          }

          table.laudo-table { width: 100% !important; border-collapse: collapse; font-size: 10pt !important; }
          table.laudo-table td { padding: 2px !important; }
          img { max-width: 100% !important; }

          .signature-row {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
          }
          
          tr {
              page-break-inside: avoid;
          }
          ::-webkit-scrollbar { display: none; }
        }

        .laudo-table { width: 100%; border-collapse: collapse; font-family: "Calibri", sans-serif; font-size: 11px; }
        .laudo-table td { border: 1px solid black; padding: 2px 4px; vertical-align: middle; }
        .no-border { border: none !important; }

        .status-select {
            font-size: 16px; font-weight: bold; border: none; outline: none;
            background: transparent; cursor: pointer; text-align: center; width: 100%;
        }
        .status-select option { background-color: white; color: black; }
        
        .btn-action {
            cursor: pointer; padding: 4px 8px; border-radius: 4px; font-size: 10px;
            font-weight: bold; transition: background-color 0.2s; border: 1px solid #ccc;
        }
        .btn-add { background-color: #e6f7ff; border-color: #1890ff; color: #1890ff; }
        .btn-add:hover { background-color: #bae7ff; }
        
        .btn-remove { background-color: #fff1f0; border-color: #ff4d4f; color: #ff4d4f; }
        .btn-remove:hover { background-color: #ffccc7; }
        
        .btn-disabled { cursor: not-allowed; opacity: 0.5; }
        
        .row-count-input {
            width: 50px; padding: 3px; font-size: 11px; text-align: center;
            border: 1px solid #ccc; border-radius: 4px; margin-right: 5px;
        }
      `}</style>

      <table className="laudo-table" cellSpacing="0" border="1">
        <colgroup>
            <col style={{width: '20%'}}/>
            <col style={{width: '30%'}}/>
            <col style={{width: '20%'}}/>
            <col style={{width: '30%'}}/>
        </colgroup>
        <tbody>
            {/* CABEÇALHO DINÂMICO CORRIGIDO */}
            <tr>
                <td colSpan={3} className="no-border" style={{ borderBottom: '2px solid black !important', borderTop: '2px solid black !important', borderLeft: '2px solid black !important', padding: '10px', verticalAlign: 'bottom' }}>
                    <div style={{ fontSize: '48px', align: 'center', fontWeight: 'bold', marginBottom: '5px' }}>CERTIFICADO DE ENSAIO</div>
                    <div style={{ fontSize: '13px' , align: 'center' }}>
                        CNPJ: {reportLayout.laudo_empresa_cnpj || '24.479.416/0001-00'}  |  Tel: {reportLayout.laudo_empresa_telefone || '(11) 93149-3201'}  |  e-mail: {reportLayout.laudo_empresa_email || 'amperaservicos@gmail.com'}
                    </div>
                </td>
                
                <td colSpan={1} className="no-border" style={{ borderBottom: '2px solid black !important', borderTop: '2px solid black !important', borderRight: '2px solid black !important', textAlign: 'right', padding: '10px', verticalAlign: 'middle' }}>
                    {reportLayout.laudo_logo_path ? (
                        <img src={getDirectImageUrl(`/api/uploads/layout_images/${reportLayout.laudo_logo_path}`)} alt="Logo" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                    ) : (
                        <img src={logoLaudoDefault} alt="Logo" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                    )}
                </td>
            </tr>

            {/* DADOS SOLICITANTE */}
            <tr><td colSpan={4} className="no-border" style={{height: '5px'}}></td></tr>
            <tr>
                <td colSpan={4} style={{ backgroundColor: '#bfbfbf', fontWeight: 'bold', border: '2px solid black', fontSize: '14px' }}>
                    DADOS DO SOLICITANTE:
                </td>
            </tr>
            <tr>
                <td colSpan={4} style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#d9d9d9', border: '2px solid black', padding: '10px' }}>
                    <InputCell field="cliente_nome" {...commonProps} style={{fontSize: '24px', textAlign: 'center'}} />
                </td>
            </tr>

            {/* Tabela de Dados Principais */}
            <tr style={{fontSize: '14px'}}>
                <td style={{fontWeight: 'bold'}}>Pedido:</td>
                <td><InputCell field="pedido" {...commonProps} align="left" /></td>
                <td style={{fontWeight: 'bold'}}>Data do Ensaio:</td>
                <td><InputCell field="data_ensaio" type="date" {...commonProps} align="left" /></td>
            </tr>
            <tr style={{fontSize: '14px'}}>
                <td style={{fontWeight: 'bold'}}>CNPJ:</td>
                <td><InputCell field="cliente_cnpj" {...commonProps} align="left" /></td>
                <td style={{fontWeight: 'bold'}}>Local:</td>
                <td><InputCell field="local" {...commonProps} align="left" /></td>
            </tr>
            <tr style={{fontSize: '14px'}}>
                <td style={{fontWeight: 'bold'}}>Temperatura:</td>
                <td><InputCell field="temperatura" {...commonProps} align="left" /></td>
                <td style={{fontWeight: 'bold'}}>Umidade Relativa:</td>
                <td><InputCell field="umidade" {...commonProps} align="left" /></td>
            </tr>

            {/* ENSAIOS REALIZADOS */}
            <tr style={{fontSize: '14px'}}>
                <td colSpan={2} style={{ borderBottom: '2px solid black' }}>
                    <b>Ensaios Realizados:</b><br/>
                    1 - VISUAL 2 - DIMENSIONAL 3 - ELÉTRICO
                </td>
                <td colSpan={2} style={{ borderBottom: '2px solid black', textAlign: 'right' }}>
                    <b>Norma Aplicável:</b><br/>
                    <InputCell field="norma_aplicavel" {...commonProps} placeholder="Ex: ASTM F 711" align="right" />
                </td>
            </tr>

            {/* DESCRIÇÃO TÉCNICA */}
            <tr><td colSpan={4} className="no-border" style={{height: '10px'}}></td></tr>

            {/* 1 - VISUAL */}
            <tr style={{fontSize: '14px'}}>
                <td colSpan={4} className="no-border">
                    <b>• VISUAL</b>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="no-border" style={{fontSize: '12px', textAlign: 'justify'}}>
                    Os equipamentos abaixo especificados, apresentam aspecto satisfatório para a realização de ensaios de tensão aplicada.
                </td>
            </tr>

            {/* 2 - DIMENSIONAL */}
            <tr style={{fontSize: '14px'}}>
                <td colSpan={4} className="no-border" style={{paddingTop: '10px'}}>
                    <b>2 - DIMENSIONAL</b>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="no-border" style={{fontSize: '12px', textAlign: 'justify'}}>
                    As dimensões encontradas nos equipamentos estão discriminadas abaixo.
                </td>
            </tr>

            {/* 3 - ELÉTRICO */}
            <tr style={{fontSize: '14px'}}>
                <td colSpan={4} className="no-border" style={{paddingTop: '10px'}}>
                    <b>3 - ELÉTRICO</b>
                </td>
            </tr>
            <tr style={{fontSize: '14px'}}>
                <td colSpan={4} className="no-border">
                    <b>3.1 EQUIPAMENTO UTILIZADO</b>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="no-border" style={{fontSize: '12px'}}>
                    Testador de Alta Tensão Alternada:
                    <span style={{display: 'inline-block', width: '250px', borderBottom: '1px solid #ccc'}}>
                        <InputCell field="hipot_modelo" {...commonProps} placeholder="HIPOT 120KV..." align="left" />
                    </span>
                    Nº de Série:
                    <span style={{display: 'inline-block', width: '150px', borderBottom: '1px solid #ccc'}}>
                        <InputCell field="hipot_serie" {...commonProps} align="left" />
                    </span>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="no-border" style={{fontSize: '12px'}}>
                    Certificado de Calibração Nº
                    <span style={{display: 'inline-block', width: '150px', borderBottom: '1px solid #ccc'}}>
                        <InputCell field="hipot_certificado" {...commonProps} align="left" />
                    </span>
                    Calibrado em
                    <span style={{display: 'inline-block', width: '85px', borderBottom: '1px solid #ccc'}}>
                        <InputCell field="hipot_calibracao" type="date" {...commonProps} align="left" />
                    </span>,
                    validade:
                    <span style={{display: 'inline-block', width: '85px', borderBottom: '1px solid #ccc'}}>
                        <InputCell field="hipot_validade" type="date" {...commonProps} align="left" />
                    </span>.
                </td>
            </tr>

            <tr style={{fontSize: '14px'}}>
                <td colSpan={4} className="no-border" style={{paddingTop: '5px'}}>
                    <b>3.2 ENSAIO REALIZADO:</b>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="no-border" style={{fontSize: '12px', textAlign: 'justify'}}>
                    <textarea
                        value={safeData['descricao_ensaio'] || 'Os equipamentos foram ensaiados conforme ASTM F 711. Foi aplicada uma tensão de 50kv, a uma taxa de elevação de no máximo 3kv/s durante o tempo de 60s.'}
                        onChange={(e) => !readOnly && handleChange('descricao_ensaio', e.target.value)}
                        className="print-input"
                        style={{width: '100%', border: 'none', resize: 'none', fontFamily: 'inherit', height: '40px', background: readOnly ? 'transparent' : '#f0f8ff'}}
                        disabled={readOnly}
                    />
                </td>
            </tr>

            {/* TÍTULO DA TABELA DE EQUIPAMENTOS */}
            <tr><td colSpan={4} className="no-border" style={{height: '10px'}}></td></tr>
            <tr>
                <td colSpan={4} style={{textAlign: 'center', fontWeight: 'bold',fontSize: '14px'}}>
                    Equipamento:
                </td>
            </tr>
            <tr>
                <td colSpan={4} style={{textAlign: 'center', backgroundColor: '#d9d9d9', border: '2px solid black', padding: '5px'}}>
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px'}}>
                        <span style={{fontSize: '16px', fontWeight: 'bold', width: '60%'}}>
                            <InputCell field="nome_equipamento" {...commonProps} placeholder="Selecione o Item acima" style={{fontSize: '16px', fontWeight: 'bold', textAlign: 'center'}} />
                        </span>
                        <span style={{width: '150px'}}>
                            <select
                                value={statusValue}
                                onChange={(e) => !readOnly && handleChange('status_equipamento', e.target.value)}
                                disabled={readOnly}
                                className="status-select"
                                style={{
                                    color: isAprovado ? '#00b050' : 'red',
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                }}
                            >
                                <option value="APROVADO" style={{color: '#00b050'}}>APROVADO</option>
                                <option value="REPROVADO" style={{color: 'red'}}>REPROVADO</option>
                            </select>
                        </span>
                    </div>
                </td>
            </tr>

            {/* --- CONTROLES DE LINHA (GERENCIAMENTO) --- */}
            {!readOnly && (
                <tr className="no-print">
                    <td colSpan={4} className="no-border" style={{ padding: '5px 0' }}>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: '10px', fontSize: '12px' }}>
                                <span>Total de Linhas: </span>
                                <input 
                                    type="number" 
                                    min="1" 
                                    max="50" 
                                    value={rowCount} 
                                    onChange={handleRowCountChange}
                                    className="row-count-input"
                                />
                            </div>
                            
                            <button onClick={handleAddRow} className="btn-action btn-add">
                                + Incluir Linha
                            </button>
                            <button
                                onClick={handleRemoveRow}
                                disabled={rowCount <= 1}
                                className={`btn-action btn-remove ${rowCount <= 1 ? 'btn-disabled' : ''}`}
                            >
                                - Excluir Linha
                            </button>
                        </div>
                    </td>
                </tr>
            )}

            {/* TABELA DE ITENS - DINÂMICA (LINER / LOADBUSTER / PADRÃO) */}
            <tr>
                <td colSpan={4} style={{padding: 0, border: 'none'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                        <thead>
                            <tr style={{backgroundColor: 'black', fontSize: '14px', color: 'white', fontWeight: 'bold', textAlign: 'center'}}>
                                <th style={{border: '1px solid black', padding: '2px', width: '5%'}}>Nº</th>
                                <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>LOTE</th>
                                <th style={{border: '1px solid black', padding: '2px', width: '10%'}}>RASTREIO</th>
                                <th style={{border: '1px solid black', padding: '2px', width: '15%'}}>
                                    {isLiner ? 'PLACA' : 'FABRICANTE'}
                                </th>
                                {!isLiner && !isLoadbuster ? (
                                    <>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>COR</th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>CLASSE</th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>TENSÃO</th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>C.A</th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>TIPO</th>
                                    </>
                                ) : (
                                    <>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>COR</th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>
                                            {isLiner ? 'TENSÃO' : (isLoadbuster ? 'MODELO' : 'CLASSE')}
                                        </th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>
                                            {isLiner ? 'C.A' : 'TENSÃO'}
                                        </th>
                                        <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>TIPO</th>
                                    </>
                                )}
                                <th style={{border: '1px solid black', padding: '2px', width: '12%'}}>Nº SÉRIE</th>
                                <th style={{border: '1px solid black', padding: '2px', width: '10%'}}>RESULTADO</th>
                                <th style={{border: '1px solid black', padding: '2px', width: '8%'}}>RETESTE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: rowCount }, (_, idx) => {
                                const col4Key = isLiner ? 'placa' : 'fabricante';
                                const col6Key = isLiner ? 'tensao_liner' : (isLoadbuster ? 'modelo' : 'classe');
                                const col7Key = isLiner ? 'ca' : 'tensao';

                                return (
                                    <tr key={idx}>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_num`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'num', idx, `item_${idx}_num`)} />
                                        </td>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_lote`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'lote', idx, `item_${idx}_lote`)} />
                                        </td>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_rastreio`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'rastreio', idx, `item_${idx}_rastreio`)} />
                                        </td>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_${col4Key}`} {...commonProps} onContextMenu={(e) => handleRightClick(e, col4Key, idx, `item_${idx}_${col4Key}`)} />
                                        </td>
                                        {!isLiner && !isLoadbuster ? (
                                            <>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_cor`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'cor', idx, `item_${idx}_cor`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_classe`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'classe', idx, `item_${idx}_classe`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_tensao`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'tensao', idx, `item_${idx}_tensao`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_ca`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'ca', idx, `item_${idx}_ca`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_tipo`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'tipo', idx, `item_${idx}_tipo`)} /></td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_cor`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'cor', idx, `item_${idx}_cor`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_${col6Key}`} {...commonProps} onContextMenu={(e) => handleRightClick(e, col6Key, idx, `item_${idx}_${col6Key}`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_${col7Key}`} {...commonProps} onContextMenu={(e) => handleRightClick(e, col7Key, idx, `item_${idx}_${col7Key}`)} /></td>
                                                <td style={{border: '1px solid black'}}><InputCell field={`item_${idx}_tipo`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'tipo', idx, `item_${idx}_tipo`)} /></td>
                                            </>
                                        )}
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_serie`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'serie', idx, `item_${idx}_serie`)} />
                                        </td>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_resultado`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'resultado', idx, `item_${idx}_resultado`)} />
                                        </td>
                                        <td style={{border: '1px solid black'}}>
                                            <InputCell field={`item_${idx}_reteste`} {...commonProps} onContextMenu={(e) => handleRightClick(e, 'reteste', idx, `item_${idx}_reteste`)} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </td>
            </tr>

            {/* ESPAÇO ASSINATURA - FORÇANDO A NÃO QUEBRA */}
            <tr className="signature-row">
                <td colSpan={4} className="no-border" style={{height: '30px'}}></td>
            </tr>
            <tr className="signature-row">
                <td colSpan={4} className="no-border" style={{textAlign: 'center'}}>
                    <div style={{ display: 'inline-block', pageBreakInside: 'avoid' }}>
                        {/* AQUI A ASSINATURA DO ENGENHEIRO FICA DINÂMICA COM CORS RESOLVIDO */}
                        {finalEngImagem ? (
                            <img
                                src={finalEngImagem}
                                alt="Assinatura"
                                crossOrigin="anonymous" // ATENÇÃO: Essa linha é OBRIGATÓRIA para o html2canvas conseguir capturar a imagem
                                style={{ display: 'block', margin: '0 auto', maxWidth: '80px', maxHeight: '50px', objectFit: 'contain' }}
                            />
                        ) : (
                            <div style={{ height: '50px' }}></div>
                        )}
                        <div style={{borderTop: '1px solid black', display: 'inline-block', fontSize: '10px', padding: '5px 20px', marginTop: '5px'}}>
                            <b>{finalEngNome || '_________________________________'}</b><br/>
                            {finalEngFuncao || 'Engenheiro Responsável'}<br/>
                            {finalEngRegistro || 'CREA nº'}
                        </div>
                    </div>
                </td>
            </tr>

            {/* RODAPÉ DINÂMICO CORRIGIDO */}
            <tr className="signature-row">
                <td colSpan={3} style={{ border: 'none', fontSize: '14px', textAlign: 'left' }}>
                    {reportLayout.laudo_empresa_email || 'amperaservicos@gmail.com'}
                </td>
                <td colSpan={1} style={{ border: 'none', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}>
                    Tel.: {reportLayout.laudo_empresa_telefone || '(11) 9314-3201'}
                </td>
            </tr>

        </tbody>
      </table>

      </div>
    </div>
  );
};

export default LaudoEpis;